/**
 * Mobile analytics façade. Call-sites import the named helpers from here —
 * never instantiate an adapter directly. See ADR-0063.
 *
 *   import { track, identify, screen, reset, initAnalytics } from '../lib/analytics';
 *
 *   initAnalytics();                              // once at app startup
 *   identify(user.id, { plan: user.plan });       // after sign-in
 *   track('trip.created', { tripId, dayCount });  // anywhere
 *   reset();                                      // on sign-out
 *
 * Provider selection is keyed on `EXPO_PUBLIC_ANALYTICS_PROVIDER`. Today the
 * only concrete provider is `posthog`; anything else (or no value) → `noop`.
 */

import { NoopAnalytics } from './analytics';
import type {
  AnalyticsAdapter,
  AnalyticsProperties,
  AnalyticsTraits,
} from './analytics';
import { PostHogAnalytics } from './posthog-analytics';

export type {
  AnalyticsAdapter,
  AnalyticsProperties,
  AnalyticsTraits,
} from './analytics';
export { NoopAnalytics } from './analytics';
export { PostHogAnalytics } from './posthog-analytics';

export type AnalyticsProviderName = 'posthog' | 'noop';

let cachedProvider: AnalyticsProviderName | null = null;

function readProviderName(): AnalyticsProviderName {
  if (typeof process === 'undefined') return 'noop';
  const p = process.env.EXPO_PUBLIC_ANALYTICS_PROVIDER?.toLowerCase();
  return p === 'posthog' ? 'posthog' : 'noop';
}

/**
 * Returns the configured provider name. Cached at module level after the
 * first call so reads stay cheap.
 */
export function getAnalyticsProvider(): AnalyticsProviderName {
  cachedProvider ??= readProviderName();
  return cachedProvider;
}

function createAnalyticsAdapter(): AnalyticsAdapter {
  return getAnalyticsProvider() === 'posthog'
    ? new PostHogAnalytics()
    : new NoopAnalytics();
}

let adapter: AnalyticsAdapter | null = null;
let initialized = false;

function getAdapter(): AnalyticsAdapter {
  adapter ??= createAnalyticsAdapter();
  return adapter;
}

/**
 * Initialise the active analytics adapter. Idempotent — safe to call from
 * `app/_layout.tsx` next to `initObservability()`.
 */
export function initAnalytics(): Promise<void> {
  if (initialized) return Promise.resolve();
  initialized = true;
  return getAdapter().init();
}

export function identify(userId: string, traits?: AnalyticsTraits | undefined): void {
  getAdapter().identify(userId, traits);
}

export function track(event: string, properties?: AnalyticsProperties | undefined): void {
  getAdapter().track(event, properties);
}

export function screen(name: string, properties?: AnalyticsProperties | undefined): void {
  getAdapter().screen(name, properties);
}

/**
 * Route an exception through the active provider's error-tracking
 * pipeline. The public call site is `observability.reportError` /
 * `reportWarning`; this façade keeps the PostHog client private to the
 * analytics module. Gated by the analytics opt-state — see ADR-0065.
 */
export function captureException(
  error: unknown,
  properties?: AnalyticsProperties | undefined,
): void {
  getAdapter().captureException(error, properties);
}

export function reset(): void {
  getAdapter().reset();
}

export function flush(): Promise<void> {
  return getAdapter().flush();
}

/**
 * Resume analytics capture — see ADR-0065. Called by the consent bridge
 * in `app/_layout.tsx` when the user opts in. Safe before `initAnalytics`
 * resolves: `PostHogAnalytics.init` reconciles the SDK with the adapter's
 * opt-state once the client constructs.
 */
export function optInAnalytics(): void {
  getAdapter().optIn();
}

/**
 * Suspend analytics capture — see ADR-0065. Drops events at the adapter
 * boundary AND at the SDK boundary. Default state at boot until the
 * consent bridge fires.
 */
export function optOutAnalytics(): void {
  getAdapter().optOut();
}

export function isAnalyticsOptedOut(): boolean {
  return getAdapter().isOptedOut();
}

/**
 * Replace the active adapter — test-only seam. Equivalent helpers exist on
 * `places-provider` and `observability` for the same reason.
 */
export function _setAnalyticsForTests(next: AnalyticsAdapter | null): void {
  adapter = next;
  initialized = next !== null;
}

/**
 * Reset module-level state. Call from `beforeEach` in tests that need a
 * clean slate.
 */
export function _resetAnalyticsForTests(): void {
  adapter = null;
  initialized = false;
  cachedProvider = null;
}
